import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { FaLink, FaCopy, FaCheck } from "react-icons/fa";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Input, FormField, Select } from "./Form";
import { SegmentedControl } from "./Toolbar";
import { useToast } from "./Toast";
import { redirectsApi } from "../../services/toolsApi";
import {
  normalizeCampaignId,
  buildGoUrl,
  buildDestination,
  generateCampaignPrefix,
} from "../../utils/redirectPreview";
import {
  validateAliasName,
  validateLandingPath,
} from "../../utils/aliasValidation";
import { campaignTitle, findTitleClash, pickerLabels } from "../../utils/campaignTitles";

const MODE_OPTIONS = [
  { value: "new", label: "New campaign" },
  { value: "existing", label: "Add to existing" },
];

const box = {
  background: "#f8fafc",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: "12px 14px",
  fontSize: "0.8rem",
};

/**
 * CreateRedirectModal — admin tool to create a tracked redirect (campaign +
 * source, optional landing path / @subdomain / alias) with a live, code-formed
 * preview that mirrors the `go` function exactly. On create it registers the
 * campaign (idempotent, zero stats) and optionally the alias, then shows the
 * ready-to-share links. The auto-create-on-visit path is unchanged.
 */
const CreateRedirectModal = ({ isOpen, onClose, campaigns = [], onSuccess }) => {
  const toast = useToast();

  const [mode, setMode] = useState("new");
  const [friendlyName, setFriendlyName] = useState("");
  const [hubspotId, setHubspotId] = useState("");
  const [prefix, setPrefix] = useState("");
  const [existingId, setExistingId] = useState("");
  const [source, setSource] = useState("");
  const [landingPath, setLandingPath] = useState("");
  const [alias, setAlias] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setMode("new");
      setFriendlyName("");
      setHubspotId("");
      setPrefix(generateCampaignPrefix());
      setExistingId(campaigns[0]?.campaignId || "");
      setSource("");
      setLandingPath("");
      setAlias("");
      setResult(null);
      setCopied(null);
    }
    // Only seed on open; campaigns intentionally excluded so typing doesn't reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const campaignId = useMemo(() => {
    if (mode === "existing") return existingId || "";
    if (!friendlyName.trim()) return "";
    const identifier = (hubspotId.trim() || prefix).trim();
    return normalizeCampaignId(`${identifier}-${friendlyName}`);
  }, [mode, existingId, hubspotId, prefix, friendlyName]);

  // No two campaigns may share a name (A13 — Lee, 28 Sep 2026). The server refuses one
  // too; this says so while typing, and offers the campaign that already has the name.
  const nameClash = mode === "new" ? findTitleClash(friendlyName, campaigns) : null;
  const labels = useMemo(() => pickerLabels(campaigns), [campaigns]);

  const aliasCheck = alias.trim()
    ? validateAliasName(alias)
    : { valid: true, message: "" };
  const pathCheck = validateLandingPath(landingPath);
  const sourceValid = mode === "existing" ? !!source.trim() : true;
  const canSubmit =
    !!campaignId && !nameClash && aliasCheck.valid && pathCheck.valid && sourceValid && !isLoading;

  const preview = useMemo(() => {
    if (!campaignId || !pathCheck.valid) return null;
    const dest = buildDestination(campaignId, source, landingPath);
    return {
      goUrl: buildGoUrl(campaignId, source, landingPath),
      aliasUrl:
        alias.trim() && aliasCheck.valid
          ? `https://go.clozesure.com/${alias.trim().toLowerCase()}`
          : null,
      dest,
    };
  }, [campaignId, source, landingPath, alias, aliasCheck.valid, pathCheck.valid]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
    } catch (e) {
      toast.error("Could not copy link");
    }
  };

  const handleCreate = async () => {
    if (!canSubmit) return;
    setIsLoading(true);
    try {
      const reg = await redirectsApi.registerCampaign({
        campaignId,
        campaignType: source.trim() || undefined,
        displayName: mode === "new" ? friendlyName.trim() || undefined : undefined,
      });

      let aliasUrl = null;
      if (alias.trim()) {
        const cleanPath = landingPath.trim().replace(/^\/+|\/+$/g, "");
        const src = source.trim() || "default";
        const storedType = cleanPath ? `${src}/${cleanPath}` : src;
        await redirectsApi.createAlias(
          alias.trim().toLowerCase(),
          campaignId,
          storedType
        );
        aliasUrl = `https://go.clozesure.com/${alias.trim().toLowerCase()}`;
      }

      setResult({
        campaignId,
        created: reg.created,
        goUrl: buildGoUrl(campaignId, source, landingPath),
        aliasUrl,
        dest: buildDestination(campaignId, source, landingPath).url,
      });
      toast.success(
        reg.created
          ? "Redirect created and registered"
          : "Source added to existing campaign"
      );
      if (onSuccess) onSuccess();
    } catch (error) {
      // The server's own duplicate-name refusal — the rule working, not a fault.
      if (/^HTTP 409: /.test(error.message || "")) {
        toast.warning(error.message.replace(/^HTTP 409: /, ""), { duration: 10000 });
        return;
      }
      console.error("Error creating redirect:", error);
      toast.error(error.message || "Failed to create redirect", {
        debugInfo: {
          operation: "createRedirect",
          params: { campaignId, source, alias },
          error: error.stack || error.toString(),
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const LinkRow = ({ label, url, k }) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ color: "#6b7280", fontSize: "0.72rem", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <code
          style={{
            flex: 1,
            wordBreak: "break-all",
            color: "#1e3a8a",
            fontSize: "0.78rem",
          }}
        >
          {url}
        </code>
        <button
          type="button"
          className="icon-btn"
          title="Copy"
          onClick={() => copy(url, k)}
        >
          {copied === k ? <FaCheck /> : <FaCopy />}
        </button>
      </div>
    </div>
  );

  // ── Success view ──────────────────────────────────────────────────────────
  if (result) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Redirect ready" size="medium">
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: "0.85rem", color: "#374151" }}>
            {result.created
              ? "Campaign registered (0 redirects until the first visit)."
              : "Added to the existing campaign."}{" "}
            Campaign ID <b>{result.campaignId}</b>.
          </div>
          <div style={box}>
            {result.aliasUrl && (
              <LinkRow label="Short link (alias)" url={result.aliasUrl} k="r-alias" />
            )}
            <LinkRow label="Tracking link" url={result.goUrl} k="r-go" />
            <div style={{ color: "#6b7280", fontSize: "0.72rem", marginTop: 6 }}>
              Lands on <b style={{ color: "#374151" }}>{result.dest}</b>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button variant="ghost" onClick={() => setResult(null)}>
              Create another
            </Button>
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // ── Form view ─────────────────────────────────────────────────────────────
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New redirect" size="medium">
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <SegmentedControl value={mode} onChange={setMode} options={MODE_OPTIONS} variant="joined" />

        {mode === "new" ? (
          <>
            <FormField
              label="Campaign name"
              helpText="The friendly name for this campaign (e.g. Summer Sale)."
              error={nameClash ? `A campaign called "${campaignTitle(nameClash)}" already exists.` : undefined}
            >
              <Input
                value={friendlyName}
                onChange={(e) => setFriendlyName(e.target.value)}
                placeholder="e.g. Summer Sale"
                disabled={isLoading}
                error={!!nameClash}
                fullWidth
                autoFocus
              />
            </FormField>
            {nameClash && (
              <div style={{ marginTop: -8 }}>
                <Button
                  variant="link"
                  size="small"
                  onClick={() => {
                    setMode("existing");
                    setExistingId(nameClash.campaignId);
                  }}
                >
                  Add a source to "{campaignTitle(nameClash)}" instead
                </Button>
              </div>
            )}
            <FormField
              label="HubSpot campaign ID (optional)"
              helpText="Leave blank to auto-generate a collision-safe ID. Enter a HubSpot numeric ID to link to a HubSpot campaign."
            >
              <Input
                value={hubspotId}
                onChange={(e) => setHubspotId(e.target.value)}
                placeholder={`(auto: ${prefix})`}
                disabled={isLoading}
                fullWidth
              />
            </FormField>
          </>
        ) : (
          <FormField label="Campaign">
            <Select
              value={existingId}
              onChange={(e) => setExistingId(e.target.value)}
              options={campaigns.map((c) => ({
                value: c.campaignId,
                label: `${labels[c.campaignId]}  ·  ${c.campaignId}`,
              }))}
              placeholder="Select a campaign…"
              disabled={isLoading}
              fullWidth
            />
          </FormField>
        )}

        <FormField
          label={mode === "existing" ? "Source / channel" : "Source / channel (optional)"}
          helpText="The channel, e.g. instagram, agents, booklet. Becomes utm_source."
        >
          <Input
            value={source}
            onChange={(e) => setSource(e.target.value.toLowerCase())}
            placeholder={mode === "existing" ? "e.g. tiktok" : "(campaign-level)"}
            disabled={isLoading}
            fullWidth
          />
        </FormField>

        <FormField
          label="Landing path (optional)"
          helpText="Blank → homepage. A path (e.g. conveyancers) → that page. An @subdomain (e.g. @assets/brochure) → assets.clozesure.com/brochure."
          error={pathCheck.valid ? undefined : pathCheck.message}
        >
          <Input
            value={landingPath}
            onChange={(e) => setLandingPath(e.target.value.toLowerCase())}
            placeholder="(homepage)"
            disabled={isLoading}
            error={!pathCheck.valid}
            fullWidth
          />
        </FormField>

        <FormField
          label="Short alias (optional)"
          helpText="Creates go.clozesure.com/<alias> as a friendly shortcut."
          error={aliasCheck.valid ? undefined : aliasCheck.message}
        >
          <Input
            value={alias}
            onChange={(e) => setAlias(e.target.value.toLowerCase())}
            placeholder="e.g. summer"
            disabled={isLoading}
            error={!aliasCheck.valid}
            fullWidth
          />
        </FormField>

        {/* Live preview */}
        <div style={box}>
          {preview ? (
            <>
              <div style={{ color: "#6b7280", fontSize: "0.72rem", marginBottom: 6 }}>
                Campaign ID <b style={{ color: "#374151" }}>{campaignId}</b>
              </div>
              {preview.aliasUrl && (
                <LinkRow label="Short link (alias)" url={preview.aliasUrl} k="p-alias" />
              )}
              <LinkRow label="Tracking link" url={preview.goUrl} k="p-go" />
              <div style={{ color: "#6b7280", fontSize: "0.72rem", marginTop: 4 }}>
                Lands on <b style={{ color: "#374151" }}>{preview.dest.url}</b>
              </div>
              <div style={{ color: "#6b7280", fontSize: "0.72rem", marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span>campaign: <b style={{ color: "#374151" }}>{preview.dest.utm.utm_campaign}</b></span>
                <span>source: <b style={{ color: "#374151" }}>{preview.dest.utm.utm_source}</b></span>
                <span>medium: <b style={{ color: "#374151" }}>{preview.dest.utm.utm_medium}</b></span>
              </div>
            </>
          ) : (
            <span style={{ color: "#9ca3af" }}>
              Fill in a {mode === "new" ? "campaign name" : "campaign"} to see the generated links.
            </span>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<FaLink />}
            onClick={handleCreate}
            disabled={!canSubmit}
          >
            {isLoading ? "Creating…" : "Create redirect"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

CreateRedirectModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  campaigns: PropTypes.array,
  onSuccess: PropTypes.func,
};

export default CreateRedirectModal;
